// --- imports ---
import React, { Component, useEffect, useRef, useState } from "react";
import Loader from "../../utils/Loader";
import Footer from "../../common_components/Footer";
import Header from "../../common_components/Header";
import { withRouter } from "../../utils/withRouter";
import { connect } from "react-redux";
import { BASE_URL, toastError } from "../../utils/constants";
import { Modal } from "react-bootstrap";
import { getCmsContent_action } from "../../actions/cms_action/cmsAction";

import "pdfjs-dist/build/pdf.worker.entry";
import * as pdfjsLib from "pdfjs-dist";

console.log("[CMS/Index Module] loaded -> OK");

// 🛠️ INTERNAL COMPONENT: CDN-Based Secure Plyr Wrapper
// This bypasses Webpack 5 errors by loading Plyr from CDN at runtime.
const PlyrComponent = ({ source, options }) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const [libLoaded, setLibLoaded] = useState(false);

    useEffect(() => {
        // 1. Check if Plyr is already loaded globally
        if (window.Plyr) {
            setLibLoaded(true);
            return;
        }

        // 2. Load CSS
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://cdn.plyr.io/3.7.8/plyr.css";
        document.head.appendChild(link);

        // 3. Load JS
        const script = document.createElement("script");
        script.src = "https://cdn.plyr.io/3.7.8/plyr.js";
        script.async = true;
        script.onload = () => setLibLoaded(true);
        document.body.appendChild(script);

        // Cleanup script on unmount (optional, usually kept for cache)
    }, []);

    // Initialize Player when Lib is Loaded and Ref is ready
    useEffect(() => {
        if (libLoaded && videoRef.current && !playerRef.current && window.Plyr) {
            playerRef.current = new window.Plyr(videoRef.current, options);
            playerRef.current.source = source;
        }

        // Handle Source Updates
        if (playerRef.current) {
            playerRef.current.source = source;
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [libLoaded, source, options]);

    return (
        <div style={{ width: '100%', height: '100%' }}>
            {!libLoaded && <div style={{ color: 'white' }}>Loading Player...</div>}
            <video
                ref={videoRef}
                className="plyr"
                playsInline
                controls
                crossOrigin="anonymous"
            />
        </div>
    );
};

class CmsIndex extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isLoading: false,
            currentIndex: 0,
            cmsSectionData: [],
            MenuID: "",
            MenuUrl: "",
            cmsMenuData: [],
            cmsFooterData: [],
            cmsBannerData: [],
            isBannerOpen: false,
            cmsSubheaderData: [],
            downloadUrl: "",
            isHomePage: false,

            // ✅ PDF Viewer State
            pdfModalOpen: false,
            pdfFileUrl: null,
            numPages: 0,

            // ✅ Secure Video Player State
            videoModalOpen: false,
            videoUrl: null,
            urlType: null,
            videoReady: false,
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);

        const sortUrl = BASE_URL.split("/api");
        this.setState({ downloadUrl: sortUrl[0] });

        if (this.props.location.pathname === "/") {
            this.setState(
                { MenuUrl: "home", MenuID: 1, cmsMenuData: [], isHomePage: true },
                () => this.getCmsContent(1, "home")
            );
            return;
        }

        const path = this.props.location.pathname;
        const parts = path.split("&");
        if (parts.length === 2) {
            this.setState(
                { MenuUrl: parts[0], MenuID: parts[1], cmsMenuData: [] },
                () => this.getCmsContent(parts[1], parts[0])
            );
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            window.scrollTo(0, 0);

            const path = this.props.location.pathname;
            const parts = path.split("&");

            if (path === "/") {
                this.setState(
                    { MenuUrl: "home", MenuID: 1, cmsMenuData: [], isHomePage: true },
                    () => this.getCmsContent(1, "home")
                );
            } else if (parts.length === 2) {
                if (parts[1] === "1" && !this.state.isHomePage) {
                    window.location.href = "/";
                    return;
                }
                this.setState(
                    { MenuUrl: parts[0], MenuID: parts[1], cmsMenuData: [], isHomePage: false },
                    () => this.getCmsContent(parts[1], parts[0])
                );
            }
        }
    }

    async getCmsContent(MenuID, MenuUrl) {
        const formattedUrl = MenuUrl?.startsWith("/") ? MenuUrl.substring(1) : MenuUrl;
        try {
            this.setState({ isLoading: true });

            const data = await this.props.getCmsContent_action(MenuID || 1, formattedUrl || "home");

            if (data.error !== 1) {
                this.setState(
                    {
                        isLoading: false,
                        cmsSectionData: data?.data[0] || [],
                        cmsMenuData: data?.data[1] || [],
                        cmsFooterData: data?.data[2]?.[0] || [],
                        cmsUploadData: data?.data[3] || [],
                        cmsSubheaderData: data?.data[4] || [],
                        cmsBannerData: data?.data[5]?.[0] || [],
                        isBannerOpen: data?.data?.[5]?.[0]?.isActive && this.state.isHomePage
                    },
                    () => setTimeout(() => this.runDynamicScripts(), 600)
                );
            } else {
                toastError(data.msg);
                this.setState({ isLoading: false });
                if (data.msg === "Page not found.") this.props.navigate("/");
            }
        } catch (err) {
            console.error(err);
            this.setState({ isLoading: false });
        }
    }

    // 🛠️ HELPER: Generate Safe Plyr Source Object
    getVideoSource(url) {
        if (!url) return null;

        // 1. YouTube Detection
        const ytMatch = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
        if (ytMatch && ytMatch[2].length === 11) {
            return {
                type: "video",
                sources: [
                    {
                        src: ytMatch[2], // Send ONLY the ID
                        provider: "youtube",
                    },
                ],
            };
        }

        // 2. Fallback: MP4 / File Detection
        return {
            type: "video",
            sources: [
                {
                    src: url,
                    type: "video/mp4",
                },
            ],
        };
    }

    runDynamicScripts() {
        //STEP 0 — Convert <a> → <span>
        // 🟢 PRE-CHECK: Detect if we are on the MediaKit page
        const isMediaKitPage = window.location.href.toLowerCase().includes("mediakit");

        // We use a regular for loop or convert to array to avoid issues while modifying DOM live
        const anchors = Array.from(document.querySelectorAll("a[href]"));

        anchors.forEach(a => {
            const href = a.getAttribute("href") || "";

            // allow router + internal (if you want these to stay as real links)
            if (href.startsWith("/") || href.startsWith("#/")) return;

            // MediaKit exception
            if (isMediaKitPage) return;

            // 🛑 kill dummy anchors
            if (href === "#" || href.startsWith("javascript:")) {
                a.removeAttribute("href");
                return;
            }

            const isVideo =
                href.match(/\.(mp4|webm|mkv|mov|m3u8)$/i) ||
                href.includes("youtube.com") ||
                href.includes("youtu.be") ||
                href.includes("vimeo.com") ||
                href.includes("dailymotion.com") ||
                href.includes("facebook.com");

            const isDoc =
                href.match(/\.(pdf|jpg|jpeg|png)$/i);

            // External NON video/doc → leave untouched (Let them be normal links)
            if (!isVideo && !isDoc && /^https?:\/\//i.test(href)) return;

            // 🔥 HARD NEUTRALIZE: REPLACE <a> WITH <span>
            const span = document.createElement("span");
            
            // 1. Copy inner HTML (text, images, icons)
            span.innerHTML = a.innerHTML;

            // 2. Copy all attributes (classes, IDs, data-attributes) EXCEPT href/target
            Array.from(a.attributes).forEach(attr => {
                if (attr.name !== 'href' && attr.name !== 'target') {
                    span.setAttribute(attr.name, attr.value);
                }
            });

            // 3. Set the data-href for your click listeners later
            span.setAttribute("data-href", href);

            // 4. visual styling to mimic a link behavior
            span.style.cursor = "pointer";
            span.classList.add("no-link-preview");

            // 5. Replace the <a> with the new <span> in the DOM
            if (a.parentNode) {
                a.parentNode.replaceChild(span, a);
            }
        });
        // ... [Your existing SlideToggle, Preloader, WOW, AOS, Menu code] ...
        //STEP 1 — Helper: slideToggle
        const slideToggle = (el, duration = 300) => {
            if (window.getComputedStyle(el).display === "none") {
                el.style.display = "block";
                const h = el.scrollHeight;
                el.style.overflow = "hidden";
                el.style.maxHeight = 0;
                el.style.transition = `max-height ${duration}ms ease-in-out`;
                setTimeout(() => (el.style.maxHeight = h + "px"), 10);
                setTimeout(() => {
                    el.style.removeProperty("max-height");
                    el.style.removeProperty("overflow");
                    el.style.removeProperty("transition");
                }, duration);
            } else {
                el.style.overflow = "hidden";
                el.style.maxHeight = el.scrollHeight + "px";
                el.style.transition = `max-height ${duration}ms ease-in-out`;
                setTimeout(() => (el.style.maxHeight = 0), 10);
                setTimeout(() => {
                    el.style.display = "none";
                    el.style.removeProperty("max-height");
                    el.style.removeProperty("overflow");
                    el.style.removeProperty("transition");
                }, duration);
            }
        };

        //STEP 2 — Preloader
        const preloader = document.getElementById("preloader");
        if (preloader) {
            preloader.style.opacity = "0";
            setTimeout(() => (preloader.style.display = "none"), 500);
        }

        //STEP 3 — WOW & AOS
        if (typeof window.WOW === "function") new window.WOW()?.init();
        if (typeof window.AOS === "object") window.AOS?.init({ duration: 1000, mirror: true });

        //STEP 4 — Mobile Menu
        const mobileMenuContainer = document.querySelector(".tgmobile__menu");

        if (mobileMenuContainer) {
            document.querySelectorAll(".tgmenu__wrap .navigation li.menu-item-has-children").forEach(li => {
                if (!li.querySelector(".dropdown-btn")) {
                    li.insertAdjacentHTML("beforeend", '<div class="dropdown-btn"><span class="plus-line"></span></div>');
                }
            });

            const mobileMenuOuter = document.querySelector(".tgmobile__menu-box .tgmobile__menu-outer");
            if (mobileMenuOuter && !mobileMenuOuter.innerHTML) {
                const mainMenu = document.querySelector(".tgmenu__wrap .tgmenu__main-menu")?.innerHTML;
                if (mainMenu) mobileMenuOuter.innerHTML = mainMenu;
            }

            document.querySelectorAll(".tgmobile__menu li.menu-item-has-children .dropdown-btn").forEach(btn => {
                btn.addEventListener("click", function () {
                    this.classList.toggle("open");
                    const submenu = this.previousElementSibling;
                    if (submenu && submenu.tagName === "UL") slideToggle(submenu, 300);
                });
            });

            document.querySelector(".mobile-nav-toggler")?.addEventListener("click", () => {
                document.body.classList.add("mobile-menu-visible");
            });

            document.querySelectorAll(".tgmobile__menu-backdrop, .tgmobile__menu .close-btn").forEach(el =>
                el.addEventListener("click", () => document.body.classList.remove("mobile-menu-visible"))
            );
        }

        // STEP 5 — Sticky Header
        window.addEventListener("scroll", () => {
            const y = window.scrollY;
            const sticky = document.getElementById("sticky-header");
            const scrollBtn = document.querySelector(".scroll-to-target");
            const headerHeight = document.getElementById("header-fixed-height");

            if (y < 245) {
                sticky?.classList.remove("sticky-menu");
                scrollBtn?.classList.remove("open");
                headerHeight?.classList.remove("active-height");
            } else {
                sticky?.classList.add("sticky-menu");
                scrollBtn?.classList.add("open");
                headerHeight?.classList.add("active-height");
            }
        });

        document.querySelector(".scroll-to-target")?.addEventListener("click", function () {
            const t = document.querySelector(this.getAttribute("data-target"));
            if (t) window.scrollTo({ top: t.offsetTop, behavior: "smooth" });
        });

        //STEP 6 — Backgrounds
        document.querySelectorAll("[data-background]").forEach(el => {
            el.style.backgroundImage = `url(${el.getAttribute("data-background")})`;
        });
        document.querySelectorAll("[data-bg-color]").forEach(el => {
            el.style.backgroundColor = el.getAttribute("data-bg-color");
        });

        //STEP 7 — Search Popup
        document.querySelector(".search-open-btn")?.addEventListener("click", () => {
            document.querySelector(".search__popup")?.classList.add("search-opened");
            document.querySelector(".search-popup-overlay")?.classList.add("search-popup-overlay-open");
        });

        document.querySelector(".search-close-btn")?.addEventListener("click", () => {
            document.querySelector(".search__popup")?.classList.remove("search-opened");
            document.querySelector(".search-popup-overlay")?.classList.remove("search-popup-overlay-open");
        });

        //STEP 8 — Offcanvas Menu
        document.querySelector(".menu-tigger")?.addEventListener("click", e => {
            e.preventDefault();
            document.querySelector(".offCanvas__info")?.classList.add("active");
            document.querySelector(".offCanvas__overly")?.classList.add("active");
        });

        document.querySelectorAll(".menu-close, .offCanvas__overly").forEach(el => {
            el.addEventListener("click", () => {
                document.querySelector(".offCanvas__info")?.classList.remove("active");
                document.querySelector(".offCanvas__overly")?.classList.remove("active");
                document.querySelector(".offCanvas__info1")?.classList.remove("active");
            });
        });

        //STEP 9 — Swiper
        if (window.Swiper) {
            const initSwiper = (selector, options) => {
                const el = document.querySelector(selector);
                if (!el) return;
                if (el.swiper) return; // Prevent double-initialization
                try {
                    el.swiper = new window.Swiper(selector, options);
                } catch (err) {
                    console.error("Swiper init error on", selector, err);
                }
            };

            initSwiper('.slider-active', {
                spaceBetween: 0,
                effect: "fade",
                loop: true,
                autoplay: { delay: 6000 }
            });

            initSwiper('.slider-active-two', {
                spaceBetween: 0,
                effect: "fade",
                navigation: {
                    nextEl: ".slider-button-next",
                    prevEl: ".slider-button-prev"
                }
            });

            initSwiper('.slider-active-three', {
                spaceBetween: 0,
                effect: "fade",
                loop: true,
                autoplay: { delay: 6000 }
            });

            initSwiper('.project_active', {
                slidesPerView: 3,
                spaceBetween: 24,
                loop: true,
                navigation: {
                    nextEl: ".project-button-next",
                    prevEl: ".project-button-prev"
                },
                breakpoints: {
                    0: { slidesPerView: 1 },
                    576: { slidesPerView: 1 },
                    768: { slidesPerView: 2 },
                    992: { slidesPerView: 3 }
                }
            });

            initSwiper('.brand-active', {
                slidesPerView: 5,
                spaceBetween: 24,
                loop: true,
                breakpoints: {
                    0: { slidesPerView: 2 },
                    576: { slidesPerView: 3 },
                    768: { slidesPerView: 3 },
                    992: { slidesPerView: 4 },
                    1200: { slidesPerView: 5 }
                }
            });

            initSwiper('.testimonial__active', {
                slidesPerView: 2,
                spaceBetween: 24,
                loop: true,
                autoplay: { delay: 6000 },
                navigation: {
                    nextEl: ".testimonial-button-next",
                    prevEl: ".testimonial-button-prev"
                },
                breakpoints: {
                    0: { slidesPerView: 1 },
                    576: { slidesPerView: 1 },
                    768: { slidesPerView: 2 }
                }
            });

            initSwiper('.AwardSwiper', {
                slidesPerView: 3,
                spaceBetween: 24,
                loop: true,
                autoplay: { delay: 6000 },
                navigation: {
                    nextEl: ".testimonial-button-next",
                    prevEl: ".testimonial-button-prev"
                },
                breakpoints: {
                    0: { slidesPerView: 1 },
                    576: { slidesPerView: 1 },
                    768: { slidesPerView: 2 },
                    992: { slidesPerView: 3 }
                }
            });
        }


        //ISOTOPE
        if (window.Isotope && window.imagesLoaded) {
            const grid = document.querySelector('.project-active-two');
            if (grid) {
                window.imagesLoaded(grid, () => {
                    const iso = new window.Isotope(grid, {
                        itemSelector: '.grid-item',
                        percentPosition: true,
                        masonry: { columnWidth: '.grid-item' }
                    });

                    document.querySelectorAll('.project__menu-nav button').forEach(btn => {
                        btn.addEventListener('click', function (e) {
                            e.preventDefault();

                            const filterValue = this.getAttribute('data-filter');
                            iso.arrange({ filter: filterValue });

                            document.querySelector('.project__menu-nav button.active')?.classList.remove('active');
                            this.classList.add('active');
                        });
                    });
                });
            }
        }

        //STEP 11 — FULL COUNTDOWN SYSTEM
        const setupCountdown = (config) => {
            const targetDate = new Date(config.date);
            let interval = null;

            const update = () => {
                const now = new Date();
                const remaining = targetDate - now;

                if (remaining <= 0) {
                    if (interval) clearInterval(interval);
                    if (config.days) config.days.textContent = "00";
                    if (config.hours) config.hours.textContent = "00";
                    if (config.minutes) config.minutes.textContent = "00";
                    if (config.seconds) config.seconds.textContent = "00";
                    return;
                }

                const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
                const hours = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

                if (config.days) config.days.textContent = days.toString().padStart(2, "0");
                if (config.hours) config.hours.textContent = hours.toString().padStart(2, "0");
                if (config.minutes) config.minutes.textContent = minutes.toString().padStart(2, "0");
                if (config.seconds) config.seconds.textContent = seconds.toString().padStart(2, "0");
            };

            interval = setInterval(update, 1000);
            update();

            // trigger
            document.querySelectorAll(config.triggerSelector).forEach(trigger => {
                trigger.addEventListener("click", (e) => {
                    e.preventDefault();
                    if (new Date() >= targetDate) {
                        window.open(config.redirectUrl, "_blank");
                    } else {
                        document.getElementById(config.modalId)?.classList.add("active");
                    }
                });
            });

            document.getElementById(config.closeId)?.addEventListener("click", () => {
                document.getElementById(config.modalId)?.classList.remove("active");
            });
        };

        // Your 3 countdowns
        setupCountdown({
            date: "2025-02-21T12:00:00",
            triggerSelector: ".register_click, .booking_click",
            modalId: "timer_Modal1",
            closeId: "close_timer_Modal1",
            redirectUrl: "https://india-itme.com/#/ITME2026",
            days: document.querySelector(".days1 .number1"),
            hours: document.querySelector(".hours1 .number1"),
            minutes: document.querySelector(".minutes1 .number1"),
            seconds: document.querySelector(".seconds1 .number1"),
        });

        setupCountdown({
            date: "2026-12-31T12:00:00",
            triggerSelector: ".visitor_header_footer_click",  // exception link
            modalId: "timer_Modal",
            closeId: "close_timer_Modal",
            redirectUrl: "https://india-itme.com/#/visitorregistration/ITME2026",
            days: document.querySelector(".days .number"),
            hours: document.querySelector(".hours .number"),
            minutes: document.querySelector(".minutes .number"),
            seconds: document.querySelector(".seconds .number"),
        });

        setupCountdown({
            date: "2025-02-21T12:00:00",
            triggerSelector: ".login_btn_click",
            modalId: "timer_Modal2",
            closeId: "close_timer_Modal2",
            redirectUrl: "https://india-itme.com/",
            days: document.querySelector(".days2 .number2"),
            hours: document.querySelector(".hours2 .number2"),
            minutes: document.querySelector(".minutes2 .number2"),
            seconds: document.querySelector(".seconds2 .number2"),
        });

        //STEP 12 — jQuery Plugins
        if (window.jQuery) {
            const $ = window.jQuery;

            if ($.fn.magnificPopup) {
                $(".popup-image").magnificPopup({ type: "image", gallery: { enabled: true } });
            }

            if ($.fn.marquee) {
                $(".marquee_mode").marquee({ speed: 20, gap: 35, direction: "left", duplicated: true, pauseOnHover: true });
            }
        }

        //STEP 13 — PDF / IMAGE Viewer (UPDATED)
        document.querySelectorAll('span[data-href]').forEach(span => {
            const url = span.getAttribute("data-href") || "";

            // Check for PDF (Case Insensitive)
            if (url.match(/\.pdf$/i) || url.match(/\.pdf\?/i)) {
                span.addEventListener("click", () => this.openPdfViewer(url, 1));
            }

            // Check for Images (Case Insensitive)
            if (url.match(/\.(jpg|jpeg|png)$/i) || url.match(/\.(jpg|jpeg|png)\?/i)) {
                span.addEventListener("click", () => this.openPdfViewer(url, 2));
            }
        });

        // STEP 13.1 — VIDEO DETECTION (Updated for Plyr)
        document.querySelectorAll('span[data-href]').forEach(span => {
            let url = span.getAttribute("data-href") || "";

            const isVideo =
                url.match(/\.(mp4|webm|mkv|mov|m3u8)$/i) ||
                url.includes("youtube.com") ||
                url.includes("youtu.be") ||
                url.includes("vimeo.com") ||
                url.includes("dailymotion.com") ||
                url.includes("facebook.com");

            if (isVideo) {
                // Clean tracking params
                if (url.includes("?si=")) url = url.split("?si=")[0];
                if (url.includes("&t=")) url = url.split("&t=")[0];

                span.addEventListener("click", (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log("%c[VIDEO] Loading via Plyr CDN →", "color:green;font-weight:bold;", url);

                    this.setState({
                        videoModalOpen: true,
                        videoUrl: url,
                        videoReady: false // Reset ready state
                    });
                });
            }
        });

        // STEP 14 — Internal Anchor Scroll (SAFE)
        document.querySelectorAll('span[data-href^="#"]').forEach(span => {
            const href = span.getAttribute("data-href");

            // 🛑 HARD GUARDS
            if (!href || href === "#") return;
            if (href.startsWith("#/")) return;
            if (href.includes("/")) return;

            span.addEventListener("click", e => {
                e.preventDefault();
                e.stopPropagation();

                const target = document.querySelector(href);
                if (!target) return;

                const offset = document.getElementById("site-header")?.offsetHeight || 80;
                const y = target.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top: y, behavior: "smooth" });
            });
        });

        //STEP 15 — External URLs BUT EXCLUDE VIDEO/DOC HANDLED LINKS
        document.querySelectorAll('span[data-href^="http"]').forEach(span => {
            const href = span.getAttribute("data-href") || "";

            const isVideo =
                href.match(/\.(mp4|webm|mkv|mov|m3u8)$/i) ||
                href.includes("youtube.com") ||
                href.includes("youtu.be") ||
                href.includes("vimeo.com") ||
                href.includes("dailymotion.com") ||
                href.includes("facebook.com");

            // 🟢 Stop redirecting externally for VIDEO or DOC links
            const isDocLink = href.match(/\.(pdf|jpg|jpeg|png)$/i);

            if (isVideo || isDocLink) return;

            // Otherwise, open strictly external generic links
            span.addEventListener("click", () => window.open(href, "_blank"));
        });

        // STEP 16 — CSS helper (Updated for Plyr Security)
        const style = document.createElement("style");
        style.textContent = `
            .no-link-preview { text-decoration: none; color: inherit; }
            .no-link-preview:hover { opacity: 0.85; }

            /* 🛡️ HIDE PLYR DOWNLOAD BUTTONS (Extra Safety) */
            .plyr__download { display: none !important; }
            
            /* 🛡️ PREVENT SELECTION */
            .noselect {
                -webkit-touch-callout: none;
                -webkit-user-select: none;
                -khtml-user-select: none;
                -moz-user-select: none;
                -ms-user-select: none;
                user-select: none;
            }
        `;
        document.head.appendChild(style);
    }

    openPdfViewer = (url, urlType) => {
        console.log("%c[PDF] Loading →", "color:orange;font-weight:bold;", url);
        this.setState({ pdfModalOpen: true, pdfFileUrl: url, numPages: 0, urlType: urlType });
        if (urlType === 1) {
            setTimeout(() => {

                const container = document.getElementById("pdf-container");
                pdfjsLib.getDocument(url).promise.then(async (pdf) => {
                    for (let i = 1; i <= pdf.numPages; i++) {
                        const page = await pdf.getPage(i);
                        const viewport = page.getViewport({ scale: 1.5 });

                        const canvas = document.createElement("canvas");
                        const context = canvas.getContext("2d");

                        canvas.height = viewport.height;
                        canvas.width = viewport.width;

                        // Responsive styling
                        canvas.style.width = "90%";
                        canvas.style.height = "auto";
                        canvas.style.display = "block";
                        canvas.style.margin = "20px auto";

                        container.appendChild(canvas);

                        await page.render({ canvasContext: context, viewport }).promise;
                    }
                });
            }, 500);
        }
        console.log("%c[PDF] Opening →", "color:blue;font-weight:bold;", url);
    };

    closePdfViewer = () => {
        this.setState({ pdfModalOpen: false, pdfFileUrl: null, numPages: 0 });
    };

    // 🛡️ SECURITY: Block Keyboard Shortcuts
    preventInspect = (e) => {
        if (e.keyCode === 123) { // F12
            e.preventDefault();
            return false;
        }
        if (e.ctrlKey && e.shiftKey && e.keyCode === 73) { // Ctrl+Shift+I (Inspect)
            e.preventDefault();
            return false;
        }
        if (e.ctrlKey && e.keyCode === 85) { // Ctrl+U (View Source)
            e.preventDefault();
            return false;
        }
        if (e.ctrlKey && e.keyCode === 83) { // Ctrl+S (Save)
            e.preventDefault();
            return false;
        }
        if (e.ctrlKey && e.keyCode === 67) { // Ctrl+C (Copy)
            e.preventDefault();
            return false;
        }
    };

    render() {
        const { isLoading, cmsSectionData, cmsMenuData, cmsFooterData, cmsBannerData, downloadUrl } = this.state;

        // 🛠️ Generate Plyr Source Object
        const plyrSource = this.state.videoUrl ? this.getVideoSource(this.state.videoUrl) : null;

        // 🛡️ Plyr Secure Options (No Download, Minimized Branding)
        const plyrOptions = {
            autoplay: true,
            muted: true, // Required for Autoplay in modern browsers
            controls: [
                'play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'
            ], // ❌ 'download' is intentionally removed from this list
            keyboard: { focused: false, global: false }, // Disable keyboard shortcuts
            tooltips: { controls: false, seek: false },
            youtube: {
                noCookie: true,
                rel: 0,
                showinfo: 0,
                iv_load_policy: 3,
                modestbranding: 1
            }
        };

        return (
            <>
                {isLoading && <Loader />}

                <main className="main-area fix">
                    <Header headerNavs={cmsMenuData} />

                    {cmsSectionData
                        ?.filter((s) => s.isPublish && s.isactive)
                        .sort((a, b) => a.SeqNo - b.SeqNo)
                        .map((section, index) => (
                            <section key={index} className="p-0">
                                <div dangerouslySetInnerHTML={{ __html: section.SectionContent }} />
                            </section>
                        ))}

                    {cmsBannerData?.isActive && cmsBannerData.isPublish && (
                        <Modal show={this.state.isBannerOpen} centered onHide={() => this.setState({ isBannerOpen: false })}>
                            <Modal.Body className="p-0 position-relative">
                                <button className="btn-close position-absolute top-0 end-0 m-2 bg-light"
                                    onClick={() => this.setState({ isBannerOpen: false })} />
                                <img width="100%" src={downloadUrl + cmsBannerData?.BannerFilepath} alt="banner" />
                            </Modal.Body>
                        </Modal>
                    )}
                </main>

                {/* ✅ PDF Viewer (react-pdf) */}
                {this.state.pdfModalOpen && (
                    <Modal show centered size="xl" backdrop="static" keyboard={false} onHide={this.closePdfViewer}>
                        <Modal.Body
                            style={{
                                padding: 20,
                                background: "#f9f9f9",
                                textAlign: "center",
                                userSelect: "none"
                            }}
                            onContextMenu={(e) => e.preventDefault()} // disable right-click
                        >
                            <button className="btn-close" onClick={this.closePdfViewer}
                                style={{ position: "absolute", top: 10, right: 10, zIndex: 99 }} />
                            {this.state.urlType === 1 ? (

                                <div
                                    id="pdf-container"
                                    style={{
                                        width: "99%",
                                        height: "80vh",       // keeps it scrollable inside card
                                        overflowY: "auto",    // ✅ allow auto scrollbar (not forced scroll)
                                        WebkitOverflowScrolling: "touch", // ✅ smooth scroll (mobile too)
                                        background: '#f5f5f5'
                                    }}
                                ></div>
                            ) : this.state.urlType === 2 ? (
                                <img src={this.state.pdfFileUrl} style={{ pointerEvents: 'none' }} alt="Viewer" />
                            ) : <></>}
                        </Modal.Body>
                    </Modal>
                )}

                {/* 🎥 SECURE PLYR VIDEO MODAL */}
                {this.state.videoModalOpen && plyrSource && (
                    <Modal
                        show={this.state.videoModalOpen}
                        centered // 1. Centers the Modal vertically on the screen
                        size="lg"
                        backdrop="static"
                        keyboard={false}
                        onHide={() => this.setState({ videoModalOpen: false, videoUrl: null })}
                        onKeyDown={this.preventInspect}
                        className="noselect"
                    >
                        {/* Header */}

                        {/* Body */}
                        <Modal.Body
                            style={{
                                padding: 0,
                                background: "#000",
                                display: "flex",            // 2. Flexbox to center the player
                                justifyContent: "center",   // 3. Center horizontally
                                alignItems: "center",       // 4. Center vertically
                                height: "50vh",             // 5. FIXED: Specific height request
                                width: "100%",
                                position: "relative",
                                overflow: "hidden",
                                zIndex: 999
                            }}
                            onContextMenu={(e) => { e.preventDefault(); return false; }}
                        >
                            {/* Floating Close Button (Optional, since you have one in header) */}
                            <button
                                className="btn-close btn-close-white"
                                onClick={() => this.setState({ videoModalOpen: false, videoUrl: null })}
                                style={{ position: "absolute", top: 15, right: 15, zIndex: 100 }}
                            />

                            {/* Player Container */}
                            <div style={{ width: "100%", height: "100%" }}>
                                <PlyrComponent
                                    source={plyrSource}
                                    options={plyrOptions}
                                />
                            </div>
                        </Modal.Body>
                    </Modal>
                )}

                <Footer footerData={cmsFooterData} />
            </>
        );
    }
}

export default withRouter(connect(null, { getCmsContent_action })(CmsIndex));